import { loadLocaleMessages } from './loader'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, localeMeta } from './locales'
import { i18n } from './setup'
import { LOCALE_PERSIST_KEY, useLocaleStore } from './store/localeStore'
import type { SupportedLocale } from './types'

import { useEnv } from '@/shared/config'

// Module 04 — locale detection, negotiation and the single mutation path (contract 04 §8).

/** Dev-verbose gate, resolved through the typed env owner (`useEnv().MODE`) rather than a raw
 *  env read (contract 01 §5). Warnings are a development aid, so before `validateEnv()` has run
 *  (module import at cold boot, unit tests) we default to verbose; production re-syncs the real
 *  value in `setupI18n`. */
export function isDevEnv(): boolean {
  try {
    return useEnv().MODE !== 'production'
  } catch {
    return true
  }
}

/** Thrown by `setLocale` for a locale outside `SUPPORTED_LOCALES` (programmatic misuse —
 *  detection negotiates a best-fit so this never fires on the user path). Current locale is
 *  left unchanged. */
export class UnsupportedLocaleError extends Error {
  readonly i18nKey = 'errors.http.unknown'
  constructor(readonly requested: string) {
    super(`Unsupported locale: ${requested}`)
    this.name = 'UnsupportedLocaleError'
  }
}

/** Thrown by `setLocale` when a locale's message chunk fails to load. `localeStore.locale`
 *  is left unchanged (never a half-applied locale); callers surface `errors.http.network`. */
export class LocaleChunkError extends Error {
  readonly i18nKey = 'errors.http.network'
  constructor(
    readonly locale: SupportedLocale,
    cause?: unknown,
  ) {
    super(`Failed to load locale chunk: ${locale}`, cause === undefined ? undefined : { cause })
    this.name = 'LocaleChunkError'
  }
}

/** Narrow an arbitrary BCP-47-ish tag to a supported locale, case-insensitively, or `null`. */
function asSupported(tag: string): SupportedLocale | null {
  const lower = tag.toLowerCase()
  return (SUPPORTED_LOCALES as readonly string[]).includes(lower)
    ? (lower as SupportedLocale)
    : null
}

// Web globals are reached structurally through `globalThis` so the module is DOM-lib-agnostic
// (it is imported by both the DOM-typed app build and node-typed workbench/config tooling) and
// genuinely SSR/no-DOM safe (contract §14 — the guard is real, not just a type escape hatch).
interface WebGlobals {
  localStorage?: { getItem(key: string): string | null }
  document?: { documentElement: { setAttribute(name: string, value: string): void } }
}
const web = globalThis as WebGlobals

/** Read the persisted UI-locale preference straight from storage. Returns the raw string
 *  (validated by the caller) or `null` when absent/unreadable. Reads storage directly rather
 *  than the hydrated store so a fresh visitor (no persisted value) is distinguishable from one
 *  who explicitly chose the default — only the former re-negotiates. */
function readPersistedLocale(): string | null {
  const storage = web.localStorage
  if (!storage) return null
  try {
    const raw = storage.getItem(LOCALE_PERSIST_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && 'locale' in parsed) {
      const value = (parsed as { locale: unknown }).locale
      return typeof value === 'string' ? value : null
    }
    return null
  } catch {
    return null
  }
}

/** Best-fit one or more requested tags against `SUPPORTED_LOCALES`: exact match first, then
 *  a region-stripped primary-subtag match (`en-GB → en`). Returns the first hit or `null`. */
export function negotiateLocale(requested: string | readonly string[]): SupportedLocale | null {
  const list = typeof requested === 'string' ? [requested] : requested
  for (const tag of list) {
    if (!tag) continue
    const exact = asSupported(tag)
    if (exact) return exact
    const primary = tag.toLowerCase().split('-')[0]
    if (primary) {
      const stripped = asSupported(primary)
      if (stripped) return stripped
    }
  }
  return null
}

/** The locale fallback chain (contract §8), first supported layer wins:
 *   1. persisted explicit user choice (ignored + re-negotiated if no longer supported);
 *   2. authenticated workspace language (post-login only — passed in by module 06/07; `null`
 *      at cold boot);
 *   3. `DEFAULT_LOCALE` (`en`).
 *
 *  Browser/`navigator.languages` is deliberately NOT a layer (ORP-18591, ratified 2026-07-21):
 *  a session with no persisted choice starts in English regardless of browser language. With
 *  `en` the only shipped locale (product decision 2026-07-28) every layer resolves to it anyway;
 *  the chain stays in place so adding a language remains a `localeMeta` row plus a JSON set.
 *  Each layer is independently overridable so the others can be mocked in isolation. */
export function detectInitialLocale(
  input: { persisted?: string | null; workspaceLanguage?: string | null } = {},
): SupportedLocale {
  const persisted = input.persisted === undefined ? readPersistedLocale() : input.persisted
  if (persisted) {
    const fromPersisted = asSupported(persisted)
    if (fromPersisted) return fromPersisted
  }

  if (input.workspaceLanguage) {
    const fromWorkspace = negotiateLocale(input.workspaceLanguage)
    if (fromWorkspace) return fromWorkspace
  }

  return DEFAULT_LOCALE
}

/** The ONLY locale-mutation path (contract §8). Validates support, lazy-loads the bundle,
 *  swaps the active locale, syncs `localeStore`, and stamps `<html lang>`. Idempotent, and
 *  atomic: on a chunk-load failure it throws with the current locale untouched. */
export async function setLocale(target: string): Promise<void> {
  const next = asSupported(target)
  if (!next) {
    if (isDevEnv()) console.warn(`[i18n] Ignoring unsupported locale "${target}".`)
    throw new UnsupportedLocaleError(target)
  }

  const store = useLocaleStore()

  if (!store.loaded.includes(next)) {
    try {
      await loadLocaleMessages(next)
    } catch (cause) {
      if (isDevEnv())
        console.warn(`[i18n] Failed to load locale "${next}"; keeping current.`, cause)
      throw new LocaleChunkError(next, cause)
    }
    store.markLoaded(next)
  }

  // vue-i18n narrows the writable locale ref to the seeded messages' key set (`en` at boot);
  // `next` is an already-validated `SupportedLocale`, so widen the write target.
  ;(i18n.global.locale as { value: string }).value = next
  store.apply(next)
  stampHtmlLang(localeMeta[next].intl)
}

/** Reflect the active locale onto `<html lang>` for a11y + browser hyphenation. Direction is
 *  a derived mirror owned by module 05's shell watcher — not stamped here (contract §7). */
function stampHtmlLang(tag: string): void {
  web.document?.documentElement.setAttribute('lang', tag)
}
